import React, { Dispatch } from "react";
import { connect } from "react-redux";
import { RouteComponentProps, Switch } from "react-router-dom";
import { User } from "oidc-client";
import { appPaths, IAppState, ApiRequest, PageTitleSetter, appPageTitles } from "../../Common";
import { ProtectedRoute } from "../../Auth";
import {
  ApplicationFeedbackContainer,
  ApplicationFeatureRequestsContainer,
  ApplicationBugReportsContainer,
  ApplicationResourceRequestsContainer
} from "./FeedbackLists";
import { appService, SubscriptionState } from "..";
import {
  NotSubscribedToApp,
  AppNotFound,
  AppSubscriptionExpired,
  AppSubscriptionOverfull
} from "./Errors";
import ApplicationAdminContainer from "./ApplicationAdmin/ApplicationAdminContainer";
import { Role } from "../../Common/Constants/roleEnums";
import { setBreadcrumbs, setPath } from "../../Startup/Actions/StartupActions";
import { BreadcrumbData, breadcrumbService } from "../../Layout";
import { breadcrumbsMap } from "../Constants/breadcrumbsMap";
import { endpoints } from "../Constants/endpoints";
import { endpoints as feedbackEndpoints } from "../../Feedback/Constants/endpoints";
import ResponseHandler from "../ResponseHandler";
import { ResponseResult } from "../../Common/Models/ResponseResult";
import { Application } from "../Models/Application";
import ViewFeedbackContainer from "../../Feedback/Components/FeedbackDetails/ViewFeedbackContainer";
import { Feedback } from "../../Feedback";
import SubmitFeedbackContainer from "../../Feedback/Components/FeedbackForm/SubmitFeedbackContainer";

type TParams = {
  id: string;
};

type StateProps = {
  path?: string;
  appSubState: Record<string, SubscriptionState>;
  user?: User;
};

type DispatchProps = {
  setPath: (location: string) => void;
  setBreadcrumbs: (breadcrumbs: Promise<BreadcrumbData[]>) => void;
};

const mapStateToProps = (state: IAppState): StateProps => ({
  appSubState: appService.getApplicationSubStatesFromAppState(state),
  path: state.app.path,
  user: state.oidc.user
});

const mapDispatchToProps = (dispatch: Dispatch<any>): DispatchProps => ({
  setPath: (location: string) => dispatch(setPath(location)),
  setBreadcrumbs: (breadcrumbs: Promise<BreadcrumbData[]>) => dispatch(setBreadcrumbs(breadcrumbs))
});

class AppRoutes extends React.Component<RouteComponentProps<TParams> & StateProps & DispatchProps> {
  getRoutes(): JSX.Element {
    // verify the user's subscription to the app in question
    switch (this.props.appSubState[this.props.match.params.id]) {
      case SubscriptionState.Active:
        // subscription is ok- do actual routing here
        return (
          <>
            <PageTitleSetter title={appPageTitles.general} />
            <Switch>
              <ProtectedRoute
                exact
                path={appPaths.app.admin}
                component={ApplicationAdminContainer}
                roles={[Role.CMAdmin]}
              />
              <ProtectedRoute
                exact
                path={appPaths.app.addFeedback}
                component={SubmitFeedbackContainer}
              />
              <ProtectedRoute
                exact
                path={appPaths.app.featureRequests}
                component={ApplicationFeatureRequestsContainer}
              />
              <ProtectedRoute
                exact
                path={appPaths.app.bugReports}
                component={ApplicationBugReportsContainer}
              />
              <ProtectedRoute
                exact
                path={appPaths.app.resourceRequests}
                component={ApplicationResourceRequestsContainer}
              />
              <ProtectedRoute
                exact
                path={appPaths.app.viewFeedback}
                component={ViewFeedbackContainer}
              />
              <ProtectedRoute
                exact
                path={appPaths.app.viewFeatureRequest}
                component={ViewFeedbackContainer}
              />
              <ProtectedRoute
                exact
                path={appPaths.app.viewBugReport}
                component={ViewFeedbackContainer}
              />
              <ProtectedRoute
                exact
                path={appPaths.app.viewResourceRequest}
                component={ViewFeedbackContainer}
              />
              <ProtectedRoute
                exact
                path={appPaths.app.root}
                component={ApplicationFeatureRequestsContainer}
              />
            </Switch>
          </>
        );
      case SubscriptionState.Expired:
        return <ProtectedRoute path={appPaths.app.root} component={AppSubscriptionExpired} />;
      case SubscriptionState.Overfull:
        return <ProtectedRoute path={appPaths.app.root} component={AppSubscriptionOverfull} />;
      case SubscriptionState.None:
        return <ProtectedRoute path={appPaths.app.root} component={NotSubscribedToApp} />;
      default:
        return <ProtectedRoute path={appPaths.app.root} component={AppNotFound} />;
    }
  }

  resolveVariable = async (key: string, variables: Map<string, string>) => {
    switch (key) {
      case ":id":
        return this.getAppName(variables.get(key));
      case ":code":
        return this.getFeedbackName(variables.get(key));
      default:
        return key;
    }
  };

  getAppName = async (id?: string) => {
    let name;

    if (id && !!this.props.user) {
      await ApiRequest.fromPath(endpoints.applications.getByPrefix(id))
        .withAuthHeader()
        .get()
        .then(async response => {
          await ResponseHandler.handle(response, (result: ResponseResult<Application>) => {
            name = result.payload.name;
          });
        });
    }

    return name;
  };

  getFeedbackName = async (code?: string) => {
    let title;

    if (code && this.props.user) {
      await ApiRequest.fromPath(feedbackEndpoints.feedback.getTitle(code))
        .withAuthHeader()
        .get()
        .then(async response => {
          await ResponseHandler.handle(response, (result: ResponseResult<Feedback>) => {
            title = result.payload;
          });
        });
    }

    return title;
  };

  setBreadcrumbs = () => {
    if (this.props.path !== this.props.location.pathname) {
      this.props.setBreadcrumbs(
        breadcrumbService.createBreadcrumbs(
          this.props.location.pathname,
          breadcrumbsMap,
          this.resolveVariable
        )
      );
    }
  };

  render() {
    this.setBreadcrumbs();

    return this.getRoutes();
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(AppRoutes);
